import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { animalService, Animal } from '../services/animalService';
import { transferService, Transfer } from '../services/transferService';
import './Dashboard.css';

const PawIcon: React.FC<{ className?: string; size?: number }> = ({ className = '', size = 24 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className={className}
  >
    <path d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6-4c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM6 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm3.5-4C8.4 2 7.5 2.9 7.5 4s.9 2 2 2 2-.9 2-2-.9-2-2-2zm5 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM12 16c-2.2 0-4 1.8-4 4 0 .6.5 1 1 1h6c.6 0 1-.4 1-1 0-2.2-1.8-4-4-4z"/>
  </svg>
);

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const canAddAnimal = user && user.role !== 'comum';
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [transfers, setTransfers] = useState<{ sent: Transfer[]; received: Transfer[] }>({
    sent: [],
    received: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [animalsData, transfersData] = await Promise.all([
        animalService.getAnimals(),
        transferService.getTransfers(),
      ]);
      setAnimals(animalsData);
      setTransfers(transfersData);
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      // Mostrar mensagem de erro mais amigável
      if (error.response) {
        console.error('Detalhes do erro:', error.response.data);
        if (error.response.status === 401) {
          // Token inválido - será tratado pelo interceptor
          return;
        }
      } else if (error.request) {
        console.error('Erro de conexão com o servidor');
        alert('Erro ao conectar com o servidor. Verifique sua conexão.');
      } else {
        console.error('Erro ao processar requisição:', error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  const pendingTransfers = [
    ...transfers.received.filter((t) => t.status === 'pending'),
    ...transfers.sent.filter((t) => t.status === 'pending'),
  ];

  return (
    <>
      <p className="text-muted mb-4">Bem-vindo, {user?.name}!</p>

      <div className="row g-4 mb-4">
        <div className="col-sm-6 col-xl-4">
          <div className="metronic-card h-100">
            <div className="d-flex align-items-center">
              <div className="symbol symbol-50px me-3">
                <span className="symbol-label bg-light-primary">
                  <PawIcon className="text-primary" size={28} />
                </span>
              </div>
              <div>
                <span className="text-gray-600 fw-semibold d-block">Total de Animais</span>
                <span className="text-gray-900 fw-bold fs-2">{animals.length}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-xl-4">
          <div className="metronic-card h-100">
            <div className="d-flex align-items-center">
              <div className="symbol symbol-50px me-3">
                <span className="symbol-label bg-light-warning">
                  <i className="bi bi-arrow-left-right text-warning fs-2" />
                </span>
              </div>
              <div>
                <span className="text-gray-600 fw-semibold d-block">Transferências Pendentes</span>
                <span className="text-gray-900 fw-bold fs-2">{pendingTransfers.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h5 className="mb-3">Ações rápidas</h5>
      <div className="row g-4 mb-4">
        {canAddAnimal && (
          <div className="col-sm-6 col-xl-4">
            <Link to="/animals/new" className="metronic-card card-hover text-decoration-none text-dark d-block h-100">
              <i className="bi bi-plus-circle text-primary fs-1 mb-2 d-block" />
              <h6 className="fw-semibold mb-1">Cadastrar Novo Animal</h6>
              <p className="text-muted small mb-0">Registre um novo animal com microchip</p>
            </Link>
          </div>
        )}
        <div className="col-sm-6 col-xl-4">
          <Link to="/animals" className="metronic-card card-hover text-decoration-none text-dark d-block h-100">
            <PawIcon className="text-primary mb-2 d-block" size={40} />
            <h6 className="fw-semibold mb-1">Meus Animais</h6>
            <p className="text-muted small mb-0">Visualize e gerencie seus animais</p>
          </Link>
        </div>
        <div className="col-sm-6 col-xl-4">
          <Link to="/transfers" className="metronic-card card-hover text-decoration-none text-dark d-block h-100">
            <i className="bi bi-arrow-left-right text-primary fs-1 mb-2 d-block" />
            <h6 className="fw-semibold mb-1">Transferências</h6>
            <p className="text-muted small mb-0">Gerencie transferências de responsabilidade</p>
          </Link>
        </div>
        <div className="col-sm-6 col-xl-4">
          <Link to="/news" className="metronic-card card-hover text-decoration-none text-dark d-block h-100">
            <i className="bi bi-newspaper text-primary fs-1 mb-2 d-block" />
            <h6 className="fw-semibold mb-1">Notícias</h6>
            <p className="text-muted small mb-0">Central de notícias e informações</p>
          </Link>
        </div>
        <div className="col-sm-6 col-xl-4">
          <Link to="/missing-animals" className="metronic-card card-hover text-decoration-none text-dark d-block h-100">
            <i className="bi bi-search-heart text-primary fs-1 mb-2 d-block" />
            <h6 className="fw-semibold mb-1">Animais Desaparecidos</h6>
            <p className="text-muted small mb-0">Veja e reporte desaparecimentos</p>
          </Link>
        </div>
      </div>

      {pendingTransfers.length > 0 && (
        <div className="metronic-card">
          <h5 className="metronic-card-title">Transferências Pendentes</h5>
          <ul className="list-group list-group-flush">
            {pendingTransfers.map((transfer) => (
              <li key={transfer.id} className="list-group-item d-flex justify-content-between align-items-center">
                Transferência #{transfer.id}
                <span className="badge bg-warning">{transfer.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

export default Dashboard;
